import { Suspense, useState } from 'react';
import {
    Grid,
    Box,
    GridItem,
    TextField,
    AddressField,
    PhoneField,
    EmailField,
    validateEmail,
    validatePhone,
    LoadingDots
} from '@flos/react-ui';
import AccordionWizardTemplate from '../components/AccordionWizardTemplate';
import { accountHolderAtom } from '../../../recoil/atoms/accountHolderAtom';
import { useRecoilState } from 'recoil';
import { useUpdateSalesforceAccountHook } from '../../../hooks/useUpdateSalesforceAccount';
import _ from 'lodash';
import { useHippoText } from '../../../hooks/useHippoText';
import { track } from '../../../utils/trackingUtil';

const CustomerDetailsStep = ({ goNext }: { goNext: () => void }): React.JSX.Element => {
    const [accountHolderData, setAccountHolderData] = useRecoilState(accountHolderAtom);
    const [inputValues, setInputValues] = useState(accountHolderData);
    const [apiCallInProgress, setApiCallInProgress] = useState(false);
    const getHippoText = useHippoText();
    const { firstName, lastName, address, phoneNumber, email } = accountHolderData;
    const { updateSalesforceAccount } = useUpdateSalesforceAccountHook();

    const onNextClick = (): void => {
        track('Checkout contact information', [
            { firstName: inputValues.firstName },
            { lastName: inputValues.lastName },
            { address: inputValues.address },
            { phoneNumber: inputValues.phoneNumber },
            { email: inputValues.email }
        ]);

        if (!_.isEqual(accountHolderData, inputValues)) {
            setApiCallInProgress(true);
            setAccountHolderData(inputValues);
            void updateSalesforceAccount(inputValues).then(() => {
                setApiCallInProgress(false);
                goNext();
            });
        } else {
            goNext();
        }
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const { name, value } = event.target;
        setInputValues((prevFormData) => ({
            ...prevFormData,
            [name]: value
        }));
    };

    return (
        <Suspense fallback={<LoadingDots />}>
            <AccordionWizardTemplate
                disableNext={
                    !validatePhone(inputValues.phoneNumber?.replaceAll(' ', '')) ||
                    !validateEmail(inputValues.email) ||
                    apiCallInProgress
                }
                goNext={onNextClick}
            >
                <Box>
                    <Grid className="u-gap-none u-gap-4@md">
                        <GridItem span={6}>
                            <TextField
                                label={getHippoText('DSS-CONTACT-INFORMATION-FIRST-NAME')}
                                name="firstName"
                                className="u-spacing-stack-none"
                                value={firstName}
                                disabled
                                showLength={false}
                            />
                        </GridItem>
                        <GridItem span={6}>
                            <TextField
                                label={getHippoText('DSS-CONTACT-INFORMATION-LAST-NAME')}
                                name="lastName"
                                className="u-spacing-stack-none"
                                value={lastName}
                                onChange={handleInputChange}
                                disabled
                                showLength={false}
                            />
                        </GridItem>
                    </Grid>
                    <GridItem span={12}>
                        <AddressField
                            label={getHippoText('DSS-CONTACT-INFORMATION-ADDRESS')}
                            name="addressString"
                            defaultValue={address}
                            fieldEvent={() => {}}
                            disabled
                        />
                    </GridItem>
                    <GridItem span={12}>
                        <PhoneField
                            label={getHippoText('DSS-CONTACT-INFORMATION-PHONE')}
                            name="phoneNumber"
                            value={phoneNumber}
                            onChange={handleInputChange}
                            required={true}
                        />
                    </GridItem>
                    <GridItem span={12}>
                        <EmailField
                            label={getHippoText('DSS-CONTACT-INFORMATION-EMAIL')}
                            name="email"
                            value={email}
                            onChange={handleInputChange}
                            required={true}
                        />
                    </GridItem>
                    <p
                        className={
                            'u-spacing-stack-none paragraph--small u-color-supplement-grey-medium'
                        }
                    >
                        {getHippoText('DSS-CONTACT-INFORMATION-TEXT')}
                    </p>
                </Box>
            </AccordionWizardTemplate>
        </Suspense>
    );
};

export default CustomerDetailsStep;
